import { getAnnexAndWasteNameByImo } from '../utils/waste';

export type WasteItem = {
  onboardQuantity: string;
  wasteToBeDelivered: string;
  maxDedicatedStorage: string;
  estimatedAmountOfWaste: string;
  collectionCompanyName: string;
  collectionCompanyVatNumber: string;
  collectionWay: string;
  imo0183WasteCode: string;
  nextPortToDeliver: string;
};

export type AnnexDataError = {
  errorMessage: string | null;
  annexKey: string;
  wasteTypeName: string;
  wasteTypeKey: string;
};

export const validateWasteAnnexData = (
  waste: WasteItem[],
  willDischargeWaste?: boolean
): AnnexDataError[] => {
  const errors: AnnexDataError[] = waste.flatMap((wasteItem) => {
    const annexData = getAnnexAndWasteNameByImo(wasteItem.imo0183WasteCode);
    if (!annexData) {
      return [
        {
          errorMessage: `No Annex data found for waste code: ${wasteItem.imo0183WasteCode}`,
          annexKey: 'N/A',
          wasteTypeName: 'Unknown',
          wasteTypeKey: wasteItem.imo0183WasteCode,
        },
      ];
    }

    const { annex, name } = annexData;
    const maxDedicatedStorage = Number.parseFloat(
      wasteItem?.maxDedicatedStorage
    );
    const onboardQuantity = Number.parseFloat(wasteItem?.onboardQuantity);
    const estimatedAmountOfWaste = Number.parseFloat(
      wasteItem?.estimatedAmountOfWaste
    );
    let wasteToBeDelivered = Number.parseFloat(wasteItem?.wasteToBeDelivered);
    wasteToBeDelivered = willDischargeWaste ? wasteToBeDelivered : 0;

    if (
      isNaN(maxDedicatedStorage) ||
      isNaN(onboardQuantity) ||
      isNaN(wasteToBeDelivered) ||
      isNaN(estimatedAmountOfWaste)
    ) {
      return [
        {
          errorMessage: 'One or more quantity fields are invalid numbers.',
          annexKey: annex!,
          wasteTypeName: name,
          wasteTypeKey: wasteItem?.imo0183WasteCode,
        },
      ];
    }

    const wasteRetainedOnBoard = onboardQuantity - wasteToBeDelivered;
    const wasteQuantityForNextPort =
      wasteRetainedOnBoard + estimatedAmountOfWaste;
    const itemErrors: AnnexDataError[] = [];

    if (onboardQuantity < 0) {
      itemErrors.push({
        errorMessage: 'Onboard quantity cannot be negative.',
        annexKey: annex!,
        wasteTypeName: name,
        wasteTypeKey: wasteItem?.imo0183WasteCode,
      });
    }
    if (wasteToBeDelivered < 0) {
      itemErrors.push({
        errorMessage: 'Waste to be delivered cannot be negative.',
        annexKey: annex!,
        wasteTypeName: name,
        wasteTypeKey: wasteItem?.imo0183WasteCode,
      });
    }
    if (maxDedicatedStorage < 0) {
      itemErrors.push({
        errorMessage: 'Maximum dedicated storage cannot be negative.',
        annexKey: annex!,
        wasteTypeName: name,
        wasteTypeKey: wasteItem?.imo0183WasteCode,
      });
    }
    if (wasteRetainedOnBoard < 0) {
      itemErrors.push({
        errorMessage: 'Waste retained on board cannot be negative.',
        annexKey: annex!,
        wasteTypeName: name,
        wasteTypeKey: wasteItem?.imo0183WasteCode,
      });
    }
    if (estimatedAmountOfWaste < 0) {
      itemErrors.push({
        errorMessage: 'Estimated waste to be generated cannot be negative.',
        annexKey: annex!,
        wasteTypeName: name,
        wasteTypeKey: wasteItem?.imo0183WasteCode,
      });
    }
    if (wasteQuantityForNextPort < 0) {
      itemErrors.push({
        errorMessage: 'Total waste (retained + estimated) cannot be negative.',
        annexKey: annex!,
        wasteTypeName: name,
        wasteTypeKey: wasteItem?.imo0183WasteCode,
      });
    }

    if (onboardQuantity > maxDedicatedStorage) {
      itemErrors.push({
        errorMessage:
          'Onboard quantity cannot exceed the maximum storage capacity for this waste type.',
        annexKey: annex!,
        wasteTypeName: name,
        wasteTypeKey: wasteItem?.imo0183WasteCode,
      });
    }
    if (wasteToBeDelivered > onboardQuantity) {
      itemErrors.push({
        errorMessage:
          'Waste to be delivered cannot exceed the onboard quantity.',
        annexKey: annex!,
        wasteTypeName: name,
        wasteTypeKey: wasteItem?.imo0183WasteCode,
      });
    }
    if (estimatedAmountOfWaste > maxDedicatedStorage) {
      itemErrors.push({
        errorMessage:
          'Estimated waste to be generated cannot exceed the maximum storage capacity for this waste type.',
        annexKey: annex!,
        wasteTypeName: name,
        wasteTypeKey: wasteItem?.imo0183WasteCode,
      });
    }
    if (wasteQuantityForNextPort > maxDedicatedStorage) {
      itemErrors.push({
        errorMessage:
          'Total waste (retained + estimated) exceeds the maximum storage capacity for this waste type.',
        annexKey: annex!,
        wasteTypeName: name,
        wasteTypeKey: wasteItem?.imo0183WasteCode,
      });
    }
    return itemErrors;
  });
  return errors;
};
